import { replace } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import moment from 'moment';
import qs from 'qs';
import { getFormValues } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import find from 'lodash/find';
import get from 'lodash/get';

import checkPermissions from '../shared/check-permissions';
import http from '../shared/http';
import endpoints from '../../config/endpoints';
import intl from '../shared/intl';

import createCalendar, { getDateRangeFromCalendar } from '../shared/calendar';
import createTable from './components/table';
import createFilters from '../shared/filters';
import { regions } from './../../../../../enums/regions';

const calendar = createCalendar({
	namespace: 'affiliatesActivity',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const calendarSignUpAffiliate = createCalendar({
	namespace: 'affiliatesActivity',
	stateNodeName: 'calendarSignUpAffiliate',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const table =  createTable({
	namespace: 'affiliatesActivity',
	stateNodeName: 'table'
});

const filters = createFilters({
	namespace: 'affiliatesActivity',
	stateNodeName: 'filters',
	listOfFilters: [{
		name: 'offers',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'managers',
		title: 'Managers',
		placeholder: 'Add managers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: `${endpoints.LOOKUPS.ADMINS}`
		}
	}, {
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'countries',
		title: 'Countries',
		placeholder: 'Add countries',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
		}
	}, {
		name: 'affiliateRegistrationDate',
		title: 'Signed up',
		placeholder: 'Signed up',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'provided',
			source: { }
		}
	}, {
		name: 'regions',
		title: 'Regions',
		placeholder: 'Add regions',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'provided',
			source: regions.map(reg => ({
				value: reg,
				label: reg
			}))
		}
	}, {
		name: 'tags',
		title: 'Affiliate tags',
		placeholder: 'Add affiliate\'s tag',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATE_TAGS
		}
	}, {
		name: 'sale_model',
		title: 'Sale model',
		placeholder: 'Add sale model',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'static',
			source: 'root.lists.offer-sale-models'
		}
	}]
});

// actions types
const FETCH_DATA_SUCCESS = 'affiliates-activity/fetch-data-success';
const FETCH_DATA_ERROR = 'affiliates-activity/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'affiliates-activity/toggle-progress-bar';

const APPLY = 'affiliates-activity/apply';
const CLEAR = 'affiliates-activity/clear';

const CELL_CONFIG_FILTER_TOGGLE = 'affiliates-activity/cell-config-filter-toggle';

const SELECT_CURRENCY = 'affiliates-activity/select-currency';
const CHOOSE_DATE_PERIOD_FILTER = 'affiliates-activity/choose-date-period-filter';

const SET_GROUPS = 'affiliates-activity/set-groups';

// permissions
export const allowedPermissions = {
	'view': 'affiliates_activity_list'
};

// initial state

const INITIAL_CONVERSIONS_PER_DAY = process.env.CPA_ENV === 'everad' ? 50 : 1;
const INITIAL_CONVERSIONS_PER_GROUPING_PER_DAY = process.env.CPA_ENV === 'everad' ? 10 : 1;

const initialState = {
	data: null,
	calendar: calendar.initialState,
	calendarSignUpAffiliate: calendarSignUpAffiliate.initialState,
	table: table.initialState,
	showProgressBar: false,
	filters: filters.initialState,
	query: {
		conversions_per_day: INITIAL_CONVERSIONS_PER_DAY,
		conversions_per_grouping_per_day: INITIAL_CONVERSIONS_PER_GROUPING_PER_DAY,
		date_range: {
			start: null,
			end: null
		}
	},
	cellConfigFilter: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'total', placeholder: 'Total', active: false },
		{ name: 'approve', placeholder: 'Approve', active: false },
		{ name: 'accepted', placeholder: 'Appr/Cancel', active: false }
	],
	datePeriodFilter: [
		{ name: 'day', placeholder: 'Day', value: '1', active: true },
		{ name: '3day', placeholder: '3 Days', value: '3', active: false },
		{ name: 'week', placeholder: 'Week', value: '7', active: false },
		{ name: 'month', placeholder: 'Month', value: '30', active: false }
	],
	grouping: {
		options: [
			{ value: 'affiliate', label: 'Affiliate'},
			{ value: 'offer', label: 'Offer'},
			{ value: 'manager', label: 'Manager'},
			{ value: 'country', label: 'Country'},
			{ value: 'sale_model', label: 'Sales model'}
		],
		values: [
			{ value: 'affiliate', label: 'Affiliate'},
			{ value: 'offer', label: 'Offer'}
		]
	},
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: props => (dispatch, getState) => {
		const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
		const savedFilters = filters.selectors.getSavedFilters(getState(), 'affiliatesActivity');

		if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
			return dispatch(replace(`affiliates-activity/?${qs.stringify(savedFilters)}`));
		}

		return request(getState, dispatch, externalQuery).then(() => {
			if (externalQuery.entity) {
				const filtersList = externalQuery.entity.filters || [];
				dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
			}
		});
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	apply: () => (dispatch, getState) => {
		dispatch(actions.toggleProgressBar());
		setTimeout(() => dispatch({
			type: APPLY,
			payload: getFormValues('conversionsQuantityForm')(getState())
		}), 100);
	},

	clear: () => dispatch => {
		dispatch(({type: CLEAR}));
		setTimeout(() => dispatch(replace('/affiliates-activity/')), 100);
	},

	toggleCellFilter: type => ({
		type: CELL_CONFIG_FILTER_TOGGLE,
		payload: type
	}),

	selectCurrency: id => dispatch => {
		dispatch({
			type: SELECT_CURRENCY,
			payload: id
		});

		dispatch(actions.apply());
	},

	choosePeriodDateFilter: filterName => dispatch => {
		dispatch({
			type: CHOOSE_DATE_PERIOD_FILTER,
			payload: filterName
		});
		dispatch(actions.apply());
	},

	setGroups: groups => dispatch => {
		dispatch({
			type: SET_GROUPS,
			payload: groups
		});

		dispatch(actions.apply());

	}
},
calendar.actions,
calendarSignUpAffiliate.actions,
table.actions,
filters.actions
);

// reducers
const reducers = Object.assign({
	[FETCH_DATA_SUCCESS]: (state, action) => {
		return {
			...state,
			data: action.payload.data,
			query: {
				...state.query,
				date_range: getDateRangeFromCalendar(state.calendar)
			},
			showProgressBar: false,
			error: false
		};
	},
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[CELL_CONFIG_FILTER_TOGGLE]: (state, action) => {
		const filterName = action.payload;

		const activeCondition = (entity, filterName) => {
			return  (filterName === 'all')
				? entity.name === filterName
				: entity.name === 'all'
					? false
					: entity.name === filterName ? !entity.active : entity.active;
		};

		const cellConfigFilter = state.cellConfigFilter.map(
			entity => ({
				...entity,
				active: activeCondition(entity, filterName)
			})
		);

		const selectedFilter = cellConfigFilter.filter(entity => entity.active && entity.name !== 'all');

		return {
			...state,
			cellConfigFilter: (selectedFilter.length === 0 && filterName !== 'all')
				? state.cellConfigFilter : cellConfigFilter
		};

	},
	[APPLY]: (state, action) => {
		const { perDay, perGroupingPerDay } = action.payload;

		return {
			...state,
			showProgressBar: false,
			query: {
				...state.query,
				conversions_per_day: !isNaN(+perDay)
					? perDay : initialState.query['conversions_per_day'],
				conversions_per_grouping_per_day: !isNaN(+perGroupingPerDay)
					? perGroupingPerDay : initialState.query['conversions_per_grouping_per_day']
			},
			data: null
		};
	},
	[CLEAR]: state => ({
		...initialState,
		query: {
			...initialState.query,
			currency: state.query.currency
		}
	}),
	[SELECT_CURRENCY]: (state, action) => ({
		...state,
		query: {
			...state.query,
			currency: action.payload
		}
	}),
	[CHOOSE_DATE_PERIOD_FILTER]: (state, action) => {
		const datePeriodFilter = state.datePeriodFilter.map(entity => ({
			...entity,
			active: action.payload === entity.name
		}));

		return {
			...state,
			datePeriodFilter
		};
	},
	[SET_GROUPS]: (state, action) => {
		return {
			...state,
			grouping: {
				...state.grouping,
				values: action.payload
			}
		};
	}
},
calendar.reducers,
calendarSignUpAffiliate.reducers,
table.reducers,
filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.affiliatesActivity.data,
	getAffiliatesSignupFilterState: state => {
		const signUpFilter = state.affiliatesActivity.filters &&
			state.affiliatesActivity.filters.selectedFilters.length &&
			state.affiliatesActivity.filters.selectedFilters.filter((filterItem) =>  {
				if (filterItem.name === 'affiliateRegistrationDate')
					return filterItem;
			});

		return Boolean(signUpFilter && signUpFilter.length);
	},
	getProgressBar: state => state.affiliatesActivity.showProgressBar,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.affiliatesActivity.calendar,
		state => state
	)(state),
	getCalendarSignUpAffiliate: state => calendar.selectors.getCalendar(
		state => state.affiliatesActivity.calendarSignUpAffiliate,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.affiliatesActivity.filters,
		state => state
	)(state),
	getTable: table.selectors.getDate,
	getCellConfig: state => state.affiliatesActivity.cellConfigFilter,
	getCurrentCurrency: state => state.affiliatesActivity.query.currency,
	getCurrentDateRange: state => state.affiliatesActivity.query.date_range,
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getConversionQuantityInitialValues: state => ({
		perDay: state.affiliatesActivity.query.conversions_per_day,
		perGroupingPerDay: state.affiliatesActivity.query.conversions_per_grouping_per_day
	}),
	getDatePeriodFilters: state => state.affiliatesActivity.datePeriodFilter,
	getGroupSelect: state => ({
		values: state.affiliatesActivity.grouping.values,
		options: state.affiliatesActivity.grouping.options
	}),
	getError: state => state.affiliatesActivity.error
};

// functions
const getRequestQuery = (state, externalQuery = {}) => {
	const {
		query, calendar, calendarSignUpAffiliate, filters: { selectedFilters },
		datePeriodFilter, grouping: { values }
	} = cloneDeep(state);

	const shouldAttachSignupQuiry = selectors.getAffiliatesSignupFilterState({
		affiliatesActivity: cloneDeep(state)
	});

	const params = cloneDeep(query);

	const dateRange = getDateRangeFromCalendar(calendar);

	const dateRangeSignUpAffiliate = getDateRangeFromCalendar(calendarSignUpAffiliate);

	params['date_period'] = get(find(datePeriodFilter, {active: true}), 'value');

	params['groups'] = values.map(entity => entity.value);

	if (dateRange) {
		params['date_range'] = dateRange;
	}

	if (dateRangeSignUpAffiliate && shouldAttachSignupQuiry) {
		params['date_range_sign_up'] = dateRangeSignUpAffiliate;
	}

	if (selectedFilters) {
		const filters = {};
		const inverseFilters = {};

		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});

		if (!isEmpty(filters)) {
			params['filters'] = filters;
		}

		if (!isEmpty(inverseFilters)) {
			params['inverse_filters'] = inverseFilters;
		}
	}

	return qs.stringify({...params, ...externalQuery});
};

const request = (getState, dispatch, externalQuery) => {
	const query = getRequestQuery(getState().affiliatesActivity, externalQuery && externalQuery.query);
	return http({
		url: `${endpoints.AFFILIATES.ACTIVITY}?${query}`,
		method: 'get',
		actions: [
			FETCH_DATA_SUCCESS,
			FETCH_DATA_ERROR
		]
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates-activity/index.js